import PropTypes from 'prop-types';
import { useContext, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
// material
import { alpha } from '@mui/material/styles';
import {
  Avatar,
  Badge,
  Box,
  Divider,
  Icon,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography
} from '@mui/material';
// components
import { useIntl } from 'react-intl';
import { LocaleContext } from 'components/LocaleProvider';
import { getDateFnsLocale } from 'utils/formatTime';
import Scrollbar from 'components/Scroll/Scrollbar';
import MenuPopover from 'components/Menu/MenuPopover';
import MDTypography from 'components/MDTypography';

// ----------------------------------------------------------------------

function renderContent(notification) {
  const title = (
    <MDTypography variant="subtitle2">
      {notification.title}
      <MDTypography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
        &nbsp; {notification.description}
      </MDTypography>
    </MDTypography>
  );

  if (notification.type === 'JOB') {
    return {
      avatar: (
        <img
          alt={notification.title}
          src={require('../../../assets/images/icons/notifications/ic_notification_services.svg')}
        />
      ),
      title
    };
  }
  return {
    avatar: <img alt={notification.title} src={notification.avatar} />,
    title
  };
}

NotificationItem.propTypes = {
  notification: PropTypes.object.isRequired,
  iconsStyle: PropTypes.object
};

function NotificationItem({ notification, iconsStyle }) {
  const { avatar, title } = renderContent(notification);
  const localeContext = useContext(LocaleContext);

  return (
    <ListItemButton
      to="/dashboard/jobExecutions"
      disableGutters
      component={RouterLink}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px'
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'background.neutral' }}>{avatar}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={title}
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled'
            }}
          >
            <Icon sx={iconsStyle}>AccessTime</Icon>
            {formatDistanceToNow(new Date(notification.creationDate), {
              locale: getDateFnsLocale(localeContext.locale)
            })}
          </Typography>
        }
      />
    </ListItemButton>
  );
}

NotificationsPopover.propTypes = {
  iconsStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.func])
};

export default function NotificationsPopover({ iconsStyle }) {
  const { formatMessage: msg } = useIntl();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [notifications] = useState(new Map());

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (notifications.size <= 0) {
    return false;
  }
  return (
    <>
      <IconButton
        ref={anchorRef}
        aria-label={msg({ id: 'action.notification.display' })}
        size="large"
        color={open ? 'primary' : 'default'}
        onClick={handleOpen}
        sx={{
          ...(open && {
            bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.focusOpacity)
          })
        }}
      >
        <Badge badgeContent={notifications?.size} color="error">
          <Icon sx={iconsStyle}>notifications</Icon>
        </Badge>
      </IconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 360 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">{msg({ id: 'notifications.title' })}</Typography>
          </Box>
        </Box>

        <Divider />

        <Scrollbar sx={{ height: { xs: 340, sm: 'auto' } }}>
          <List disablePadding>
            {[...notifications.keys()].map((k) => (
              <NotificationItem
                key={k}
                notification={notifications.get(k)}
                iconsStyle={iconsStyle}
              />
            ))}
          </List>
        </Scrollbar>
      </MenuPopover>
    </>
  );
}
